
import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Inject, input, Input, OnDestroy, Output, ViewEncapsulation, computed, Signal, DOCUMENT, ViewChild, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MatchMediaService, MeasurementUnitsService } from 'app/services';
import { adjustDate } from 'app/shared/utils/dates';
import { getYear, parseISO } from 'date-fns';
import moment, { Moment } from 'moment-timezone';

@Component({
    selector: 'kui-datetime',
    templateUrl: './datetime.component.html',
    styleUrls: ['./datetime.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class KuiDateTimeComponent implements OnDestroy {
    @ViewChild('dp') dp: any;
    type = input<'datetime-local' | 'date' | 'time'>()

    // 'form-builder' is when the dateTime is being displayed in a form i.e. not in the content-header-bar
    inputStyle = input<'form-builder' | 'content-header-bar' | 'none'>('none');

    /* expecting ISO string */
    date = input<string>();
    max = input<string>();
    min = input<string>();
    disabled = input<string>();
    icon = input<string>();

    @HostBinding('class.disable-time') @Input() disableTime = false;

    @Output() onChange: EventEmitter<string> = new EventEmitter();

    id = Math.floor(Math.random() * 1e8);

    appendTo: Signal<string> = computed(() => this.type() !== 'time' ? 'body' : null );
    dateInstance: Signal<any> = computed(() => this.date() ? new Date(adjustDate(this.date(), true)) : null );
    minInstance: Signal<Date> = computed(() => this.min() ? this.setInstanceLimits(this.min()) : null );
    maxInstance: Signal<Date> = computed(() => this.max() ?this.setInstanceLimits(this.max()) : null );
    yearRange: Signal<string> = computed(() => { 
        const currentYear = new Date().getFullYear();
        return `${this.min() ? getYear(this.min()) : currentYear - 15}:${this.max() ? getYear(this.max()) : currentYear + 15}`;
    });
    placeholder: Signal<Date> = computed(() => this.dateInstance() ? null : this.i18n.instant('SHARED.NONE'));

    timeIsDisplayed = computed(() => this.type() === 'time' || this.type() === 'datetime-local');

    dateFormat = input<string>();
    dateDisplayFormat = computed(() => {
        if (this.dateFormat()) {
            return this.getPrimeNgDateFormat(this.dateFormat());
        } else {
            // PrimeNg21 datepicker displays this as: 1 Dec 2026
            return 'd M yy';
        }
    });

    timeFormat = input<string>();

    hourFormat = input<number>();
    hourDisplayFormat = computed(() => {;
        if (this.hourFormat()) {
            return this.hourFormat();
        } else if (this.timeFormat()) {
            return this.timeFormat().toLowerCase().includes('am/pm') ? 12 : 24 
        } else {
            return 24;
        }
    })

    showOnFocus = signal(false);

    constructor(
        @Inject(DOCUMENT) private document: Document,
        public matchmedia: MatchMediaService,
        public i18n: TranslateService,
        private units: MeasurementUnitsService
    ) {
    }

    ngOnDestroy() {
        this.setFocussedState(false);
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.dp.overlayVisible = false;

            // we set it to false on component load, then after to true on purpose
            // this is so the datepicker doesn't flash open+close on component load
            this.showOnFocus.set(true);
        });
    }

    setInstanceLimits(dateValue: Moment | string) {
        if (moment.isMoment(dateValue)) {
            // someone gave us a moment value instead of a string value...
            return parseISO((dateValue as unknown as moment.Moment).toISOString());
        } else if ((dateValue as any) instanceof Date) {
            return dateValue as unknown as Date;
        } else {
            return parseISO(dateValue);
        }
    }

    update(event: string | Date) {
        // so they datepicker stays open after a selection if made if it contains a time option
        if (this.type() === 'date') {
            this.dp.overlayVisible = false;
        }

        const eventDate = new Date(event);
        const adjustedDate = adjustDate(eventDate.toISOString());
        this.onChange.emit(adjustedDate);
    }

    onDatepickerClick(event: MouseEvent) {
        event.stopPropagation();
    }

    setFocussedState(state: boolean) {
        if (state) {
            this.document.body.classList.add('kui-datetime--open');
        } else {
            this.document.body.classList.remove('kui-datetime--open');

            // In PrimeNg21 when you want the datepicker input field (so not the icon) to toggle the datepicker calender on click, it
            // opens the calender and adds the classes; 'p-inputwrapper-focus' and 'p-focus', which are only removed when the blur() event fires.
            // we need to trigger blur() because when a user selects a date, the pop up closes, but does not blur the input/
            // We put it in a time out because otherwise blur() fires before the overlay closes, and focus jumps back to the input, adding the classes again
            // meaning when the user clicks the datepicker input field to open the calender again, it won't open as it thinks it is already open
            setTimeout(() => {
                const active = document.activeElement as HTMLElement | null;
                active?.blur();
              }, 0);
        }
    }

    // The date format tokens we have in our MeasurementUnitsService are 'moment.js' style tokens. These are used throughout the app.
    // Older versions of PrimeNG had a p-calendar component that we used, which accepted moment style date format tokens.
    // p-calendar does not exist now, they made a new component: p-datepicker, which does not accept moment style tokens.
    // So the below converts ours (moment style) to PrimeNg21 style.
    getPrimeNgDateFormat(momentFormat: string) {
        const allDateUnits = this.units.getUnitList('date').units;

        for (const key of Object.keys(allDateUnits)) {
            const item = allDateUnits[key];
          
            if (item.unit === momentFormat) {
              return item.primeNG;
            }
          }
    }
}
